<template>
  <div id="app" v-if="!showEnvelopeComplete">
    <div class="loader" v-if="isLoading">
      <div class="loader__icon"></div>
    </div>
    <template v-else>
      <app-header :title="title"></app-header>
      <router-view></router-view>
      <modal
        :header="$t('header')"
        :isInitiallyActive="allowTour"
        isLarge="true">
        <envelope-guide></envelope-guide>
        <template slot="modalButtons" slot-scope="props">
          <app-button :text="showModalButtons ? $t('functional.noThanks') : $t('functional.close')" iconName="times" :action="props.close"></app-button>
          <app-button :text="$t('functional.showMe')" iconName="chevron-right" :action="showSlideShow" classList="float-right button--cta" v-if="showModalButtons"></app-button>
        </template>
      </modal>
    </template>
  </div>
  <div v-else>
    <envelope-complete
      :status="envelopeStatus"
      :clientId="clientId"
      :companyName="companyName"
      :requestedEnvelope="requestedEnvelope"></envelope-complete>
  </div>
</template>

<script>
  import AppButton from '@/components/AppButton';
  import Header from '@/components/Header';
  import Modal from '@/components/Modal';
  import EnvelopeGuide from '@/components/EnvelopeGuide';
  import EnvelopeComplete from '@/components/EnvelopeComplete';
  import brandStyler from './utils/brand-styler';
  import { getEnvelope, envelopeViewed } from './store/api';

  export default {
    name: 'app',
    components: {
      'app-header': Header,
      AppButton,
      Modal,
      EnvelopeGuide,
      EnvelopeComplete,
    },
    data() {
      return {
        title: this.$store.state.envelope.title,
        isLoading: true,
        scrollTop: 0,
        envelopeStatus: null,
        isSigned: false,
        clientId: null,
        companyName: null,
        showModalButtons: true,
      };
    },
    beforeCreate() {
      this.requestedEnvelope = {
        envelope_fingerprint: this.$route.params.envelopeFingerprint,
        signature_fingerprint: this.$route.params.signatureFingerprint,
        preview_encrypt: this.$route.params.previewURL,
        isPreview: !!this.$route.params.previewURL,
        is_new_encrypt: this.$route.query.new_encrypt,
      };

      this.$store.commit('setRequestedEnvelope', this.requestedEnvelope);
      if (this.requestedEnvelope.isPreview) window.IS_PREVIEW = true;

      getEnvelope(this.$store).then((response) => {
        const data = response.data;
        this.isSigned = data.isSigned;
        this.clientId = data.client.client_id;
        this.envelopeStatus = data.status;
        this.companyName = data.client.company_name;
        if (!this.isSigned) {
          this.title = data.envelope_title;
          this.$store.commit('setEnvelopeDetails', { title: data.envelope_title, id: data.envelope_id });
          this.$store.commit('setCompanyConfig', { config: data.config });
          this.$store.commit('setParties', { parties: data.parties });
          this.$store.commit('setClientInfo', { info: data.client });
          this.$store.commit('setDocuments', { documents: data.documents });
          this.$store.commit('setColours', { colours: data.colours });
          if (data.hasBranding || data.logoURL) {
            this.$store.commit('setLogoURL', { logo: data.logoURL, hasBranding: data.hasBranding });
          }
          this.isLoading = false;
          envelopeViewed(this.requestedEnvelope);
        } else {
          this.isLoading = false;
        }
        this.$store.commit('setCompanyConfig', { config: data.config });
        brandStyler(data.colours);
      });
    },
    created() {
      this.$bus.$on('nextDocument', () => {
        const body = document.getElementsByTagName('body')[0];
        body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      });
      this.$bus.$on('loading', () => {
        this.isLoading = true;
      });
      this.$bus.$on('loadingStop', () => {
        this.isLoading = false;
      });
      this.$bus.$on('submitEnvelope', ({ response }) => {
        getEnvelope(this.$store).then((envelope) => {
          this.envelopeStatus = envelope.data.status;

          if (response.status === 'rejected') { // TODO This needs to be more generic, isProcessed would be better
            this.isSigned = true;
          } else if (response) {
            this.isSigned = response.status === 200;
          } else {
            this.isSigned = true;
          }

          this.isLoading = false;
          if (envelope.data.envelope_redirect_url && response !== null) {
            window.location = envelope.data.envelope_redirect_url;
          }
        });
      });
      this.$bus.$on('openedFieldPopup', () => {
        this.$store.commit('setFieldPopupState', { isActive: true });
      });
      this.$bus.$on('closedFieldPopup', () => {
        this.$store.commit('setFieldPopupState', { isActive: false });
      });
    },
    mounted() {
      if (window.outerWidth <= 575) {
        this.$store.commit('setMobile', { isMobile: true });
      }
      window.addEventListener('scroll', (event) => {
        this.toggleDocumentNav(event);
        this.toggleProgressBar(event);
      });
      this.$bus.$on('scrollBody', () => {
        const header = document.querySelectorAll('header');
        const headerHeight = header[0].firstChild.clientHeight + 70;
        const currentId = this.$store.state.activePopupId;
        const input = document.querySelectorAll(`.document-field[data-id="${currentId}"]`);
        if (input.length > 0) {
          const scrollAmount = this.getOffset(input[0]);
          this.$SmoothScroll((scrollAmount - headerHeight), 500);
        }
      });
    },
    computed: {
      allowTour() {
        return parseInt(this.$store.getters.config('company_tour_enable'), 10);
      },
      showEnvelopeComplete() {
        return this.isSigned || ['cancelled', 'rejected', 'signed', 'expired'].indexOf(this.envelopeStatus) > -1;
      },
    },
    methods: {
      getOffset(el) {
        const element = el.getBoundingClientRect();
        return element.top + (window.scrollY || window.pageYOffset);
      },
      toggleDocumentNav() {
        const windowScrollHeight = window.scrollTop || window.pageYOffset;
        if ((windowScrollHeight + window.innerHeight) >= document.body.offsetHeight) {
          this.$bus.$emit('scrolledToBottom', { isBottom: true });
        } else {
          this.$bus.$emit('scrolledToBottom', { isBottom: false });
        }
      },
      toggleProgressBar() {
        const scrollTop = window.scrollTop || window.pageYOffset;
        if (scrollTop > this.scrollTop + 20) {
          this.$bus.$emit('scrollingDown');
        } else if (scrollTop < (this.scrollTop - 20)) {
          this.$bus.$emit('scrollingUp');
        }
        if (scrollTop === 0) {
          this.$bus.$emit('scrollingUp');
        }
        this.scrollTop = scrollTop;
      },
      showSlideShow() {
        this.$bus.$emit('showSlideShow');
        this.showModalButtons = false;
      },
    },
  };
</script>

<style lang="sass">
  @import '~styles/manifest.sass';
  #app
    position: relative;
</style>



// WEBPACK FOOTER //
// App.vue?693113ca